// PublicMonitorDetail — T094
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import StatusBadge from '../components/StatusBadge';
import MetricsChart from '../components/MetricsChart';
import UptimeChart from '../components/UptimeChart';
import { getPublicMonitorDetail, type PublicMonitorDetail as PublicMonitorDetailType } from '../services/api';

export default function PublicMonitorDetail() {
  const { slug, monitorId } = useParams<{ slug: string; monitorId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<PublicMonitorDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!slug || !monitorId) return;
    const load = () => {
      getPublicMonitorDetail(slug, monitorId)
        .then(setData)
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    };
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [slug, monitorId]);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="animate-pulse bg-surface rounded-lg h-64 w-full max-w-2xl" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="bg-status-down/20 text-status-down rounded-lg px-6 py-4">
          {error || 'Monitor not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-primary">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(`/status/${slug}`)}
          className="text-text-secondary hover:text-text-primary text-sm mb-4 inline-block"
        >
          &larr; Back to status page
        </button>

        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-white text-h1">{data.name}</h1>
            <p className="text-text-muted text-sm mt-1">{data.url}</p>
          </div>
          <StatusBadge state={data.state} size="lg" />
        </div>

        {data.uptimePercent != null && (
          <div className="bg-surface rounded-lg p-4 border border-border mb-6">
            <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Uptime</p>
            <p className="text-text-primary text-lg font-semibold">{data.uptimePercent.toFixed(2)}%</p>
          </div>
        )}

        <UptimeChart metrics={data.metrics || []} />

        <MetricsChart metrics={data.metrics || []} />
      </div>
    </div>
  );
}
